/**
 * Campo de texto que escreve o formato enquanto a pessoa digita.
 *
 * Papel: desenha um `Input` que aplica a máscara de `MASCARAS` a cada tecla,
 * devolve o cursor para junto do caractere que acabou de ser digitado e entrega
 * ao formulário o valor que o schema de `@concierge/shared` espera receber.
 *
 * Como usar: o campo é controlado e deverá ser montado por um `Controller` do
 * react-hook-form, repassando o `field` inteiro:
 *
 * ```tsx
 * <Controller
 *   control={form.control}
 *   name="nomeDoCampo"
 *   render={({ field }) => <CampoMascarado formato="telefone" {...field} />}
 * />
 * ```
 *
 * Ligá-lo por `register` não funciona, embora compile. O `register` escreve o
 * valor inicial direto no `input`, sem passar pela máscara, e uma data vinda da
 * API apareceria como `2026-08-31` até a primeira tecla, quando os dígitos
 * seriam remascarados como `20/26/0831`.
 *
 * A limpeza continua sendo do schema. O telefone chega ao formulário como
 * `(11) 98765-4321`, e é o `telefoneOpcionalSchema` que o reduz a dígitos e que
 * transforma o campo apagado em `null`; tirar a pontuação aqui deixaria a regra
 * duplicada, e quem chama a API sem passar por este campo não passaria por ela.
 * Data e competência são a exceção, e a razão está no cabeçalho de `mascara.ts`.
 *
 * Este arquivo mora em `shared/ui`, que o ESLint e o `check-docs` ignoram por
 * guardar o código gerado pelo shadcn. Quem alterá-lo deverá rodar
 * `npx eslint --no-ignore apps/web/src/shared/ui/campo-mascarado.tsx` à mão.
 */
import { type ChangeEvent, type ComponentProps } from 'react';

import {
  type FormatoMascarado,
  MASCARAS,
  contarDigitosAte,
  posicaoAposMascara,
} from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { Input } from '@/shared/ui/input';

interface CampoMascaradoProps extends Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'type' | 'inputMode'
> {
  formato: FormatoMascarado;
  value: string | number | null | undefined;
  onChange: (valor: string) => void;
}

/**
 * Campo mascarado para um dos formatos de `MASCARAS`.
 *
 * O texto exibido é recalculado a partir de `value` a cada render, e não
 * guardado em estado próprio. Isso faz um `form.reset` com dados vindos da API
 * aparecer já formatado, e é o motivo de `exibir` e `extrair` precisarem se
 * desfazer: se não se desfizerem, o texto recalculado difere do que o campo
 * acabou de escrever, o React o sobrescreve, e o cursor salta para o fim.
 *
 * @param formato - Qual máscara aplicar; decide também o teclado oferecido no
 *   celular.
 * @param value - O valor que o formulário guarda. Aceita o formato canônico da
 *   API (dígitos, data ISO, número) e o texto mascarado; `null` e vazio deixam o
 *   campo em branco.
 * @param onChange - Recebe o texto mascarado, ou o ISO no caso de data e
 *   competência completas. Recebe `''` quando o campo é esvaziado.
 * @param className - Classes de quem chama, aplicadas depois das internas pelo
 *   `cn()` e por isso capazes de sobrescrevê-las.
 * @returns O `Input` com a máscara ligada.
 */
export function CampoMascarado({
  formato,
  value,
  onChange,
  className,
  ...resto
}: CampoMascaradoProps) {
  const mascara = MASCARAS[formato];
  const texto = value == null || value === '' ? '' : mascara.exibir(value);

  /**
   * Reescreve o campo com a máscara e recoloca o cursor antes de avisar o
   * formulário.
   *
   * O texto é escrito no `input` aqui mesmo, antes de o React renderizar de
   * novo, porque só neste instante ainda se sabe onde o cursor estava no texto
   * cru. Quando o render chega, o valor calculado é igual ao que já está no
   * `input`, o React não o toca, e o cursor fica onde foi posto.
   */
  function handleChange(evento: ChangeEvent<HTMLInputElement>) {
    const campo = evento.target;
    const bruto = campo.value;
    const cursor = campo.selectionStart ?? bruto.length;
    const mascarado = mascara.aplicar(bruto);

    campo.value = mascarado;

    // Mexer na seleção de um campo sem foco o traria para a frente em alguns
    // navegadores, roubando o foco de onde a pessoa está.
    if (campo.ownerDocument.activeElement === campo) {
      const posicao =
        mascara.ancora === 'fim'
          ? mascarado.length
          : posicaoAposMascara(
              mascarado,
              contarDigitosAte(bruto, cursor, mascara.ancora),
              mascara.ancora,
            );
      campo.setSelectionRange(posicao, posicao);
    }

    onChange(mascara.extrair(mascarado));
  }

  return (
    <Input
      {...resto}
      type="text"
      inputMode={mascara.modoTeclado}
      value={texto}
      onChange={handleChange}
      className={cn('tabular', className)}
    />
  );
}
